import { useEffect, useState } from "react";
import {
  Col,
  Dropdown,
  Row,
  Modal,
  message,
  Form,
  Input,
  Select,
  Tag,
} from "antd";
import axios from "axios";
import HeadingComponent from "../../components/HeadingComponent";
import CustomTable from "../../components/CustomTable";
import {
  DownloadOutlined,
  MoneyCollectOutlined,
  MoreOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { createRefund, getAllInvoices } from "./Apis/invoicesApis";
import "./index.css";

const Invoices = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const columns = [
    {
      title: "Email",
      dataIndex: "customer_email",
    },
    {
      title: "Amount",
      dataIndex: "amount_paid",
      className: "url-col",
      render: (_, record) => {
        const currency = record?.currency?.toUpperCase() || "";
        const amountInCents = record?.metadata?.amount_paid
          ? parseInt(record?.metadata?.amount_paid, 10)
          : record?.amount_paid;
        const amountInDollars = (amountInCents / 100).toFixed(2);
        return `${currency} ${amountInDollars}`;
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        const statusMap = {
          paid: "Paid",
          pending: "Pending",
          failed: "Failed",
          refunded: "Refunded",
        };

        const statusColors = {
          paid: "green",
          pending: "yellow",
          failed: "red",
          refunded: "gray",
        };
        return (
          <Tag color={`${statusColors[status]}`}>
            {statusMap[status] || status}
          </Tag>
        );
      },
    },
    {
      title: "Paid",
      dataIndex: "paid",
      render: (paid) => paid?.toString(),
    },

    {
      title: "Created",
      dataIndex: "created",
      className: "url-col",
      render: (created) => new Date(created * 1000).toLocaleString(),
    },
    {
      title: "Invoice URL",
      dataIndex: "hosted_invoice_url",
      className: "url-col",
      render: (hosted_invoice_url) => (
        <a
          href={hosted_invoice_url}
          rel="noreferrer"
          target="_blank"
          title={hosted_invoice_url}
        >
          See invoice
        </a>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const items = [
          {
            key: "1",
            label: "Refund",
            icon: <MoneyCollectOutlined />,
            onClick: () => {
              setSelectedRecord(record);
              setOpenRefundModal(true);
            },
          },

          {
            key: "2",
            label: "Download Invoice",
            icon: <DownloadOutlined />,
            onClick: () => {
              if (record?.invoice_pdf) {
                window.open(
                  record.invoice_pdf,
                  "_blank",
                  "noopener,noreferrer"
                );
              } else {
                message.warning("No invoice PDF link available.");
              }
            },
          },

          {
            key: "3",
            label: "Send Invoice Copy",
            icon: <SendOutlined />,
            onClick: async () => {
              const BASE_URL = process.env.REACT_APP_BASE_API;

              let response = await axios.get(
                `${BASE_URL}payment/send-invoice?invoice_id=${record?.invoice_id}&customer_email=${record?.customer_email}`
              );

              if (response?.data?.data?.message === "Email Sent!") {
                message.success("Invoice has been sent to customer!");
              }
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} placement="bottomRight">
            <MoreOutlined className="icons_style" />
          </Dropdown>
        );
      },
    },
  ];

  const getInvoices = async () => {
    try {
      setLoading(true);
      let response = await getAllInvoices();
      response = response.sort((a, b) => b.created - a.created); // Sort by created date in descending order
      setPayments(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getInvoices();
  }, []);

  const handleRefund = async (form_values) => {
    try {
      const BASE_URL = process.env.REACT_APP_BASE_API;
      const body = {
        invoice_id: selectedRecord?.id,
        customer: selectedRecord?.customer,
        customer_email: selectedRecord?.customer_email,
        meta_data: JSON.stringify(selectedRecord?.lines?.data[0]?.metadata),
        subscription_id: selectedRecord?.subscription ?? "",
        reason: form_values?.reason || "",
        description: form_values?.description || "",
      };
      let response = await axios.post(
        `${BASE_URL}payment/refund/${selectedRecord?.charge}`,
        body
      );
      if (response?.data?.data?.error) {
        message.error(response?.data?.data?.error);
        return;
      }
      await createRefund(response?.data?.data?.refund);
      message.success("Refund created successfully");
    } catch (error) {
      console.error("Error refunding payment:", error);
    } finally {
      setOpenRefundModal(false);
    }
  };
  const onCreate = async (values) => {
    handleRefund(values);
  };

  return (
    <>
      <Row>
        <Col xs={24} sm={8}>
          <HeadingComponent text="Invoices" />
        </Col>
      </Row>
      <br />

      <CustomTable dataSource={payments} columns={columns} loading={loading} />

      {openRefundModal && (
        <Modal
          open={openRefundModal}
          okText="Refund"
          cancelText="Cancel"
          okButtonProps={{ autoFocus: true, htmlType: "submit" }}
          onCancel={() => setOpenRefundModal(false)}
          onOk={() => {
            form.submit();
          }}
          destroyOnHidden
          modalRender={(dom) => <>{dom}</>}
        >
          <div className="refund_modal_header">
            <p>Refund payment</p>
            <p>Are you sure you want to refund this payment?</p>
            <br />
            <Form
              layout="vertical"
              form={form}
              name="form"
              clearOnDestroy
              onFinish={(values) => onCreate(values)}
            >
              <Form.Item
                name="reason"
                label="Reason"
                rules={[
                  {
                    required: true,
                    message: "Please select reason!",
                  },
                ]}
              >
                <Select
                  options={[
                    { label: "Duplicate", value: "duplicate" },
                    { label: "Fraudelent", value: "fraudelent" },
                    {
                      label: "Requested by customer",
                      value: "requested_by_customer",
                    },
                    {
                      label: "Other",
                      value: "other",
                    },
                  ]}
                  placeholder="Please select an option"
                />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                dependencies={["reason"]}
                initialValue={""}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("reason") === "other" && !value) {
                        return Promise.reject(
                          new Error("Please specify reason for refund")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Please enter detailed reason for refund"
                />
              </Form.Item>
            </Form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Invoices;
